import { useEffect, useRef, useState } from 'react';

/**
 * Formats a millisecond duration as `m:ss` (minutes not zero-padded, seconds
 * zero-padded to two digits). Negative durations clamp to "0:00".
 */
export function formatElapsed(ms: number): string {
  const clamped = Math.max(0, ms);
  const totalSeconds = Math.floor(clamped / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Tracks elapsed milliseconds since the render where `active` first became
 * true. Ticks roughly once per second while `active` is true, and freezes
 * (stops updating, but keeps its last value) once `active` goes false. The
 * interval is cleared on unmount and whenever `active` is false.
 */
export function useElapsed(active: boolean): number {
  const startRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      return;
    }

    if (startRef.current === null) {
      startRef.current = Date.now();
      setElapsed(0);
    }

    const tick = () => {
      if (startRef.current !== null) {
        setElapsed(Date.now() - startRef.current);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [active]);

  return elapsed;
}
